import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MessageCircle, Users, Building2, Sparkles, FileSpreadsheet, Mic,
  Copy as CopyIcon, Bell, Zap, TrendingUp, Phone, Check, ArrowRight, Play
} from "lucide-react";

const STEPS = [
  {
    n: 1,
    icon: MessageCircle,
    title: "Conecte seu WhatsApp",
    minutes: "2 min",
    intro: "Salve o número do estatebroker.AI e libere a conexão. Depois disso, você fala comigo direto pelo seu WhatsApp pessoal.",
    actions: [
      { text: "Ver instruções no painel de configurações", to: "/app/config", primary: true },
    ],
    tips: [
      "No sandbox de teste, você precisa enviar uma mensagem de ativação uma vez. Em produção, basta salvar o número.",
      "Cada nova mensagem que chegar do seu número será reconhecida automaticamente.",
    ],
  },
  {
    n: 2,
    icon: Users,
    title: "Suba sua carteira de leads",
    minutes: "5 min",
    intro: "Você tem 2 jeitos de começar a popular o funil. Escolhe o que for mais rápido pra você.",
    actions: [
      { text: "Importar planilha CSV ou Excel", to: "/app/leads", primary: true },
      { text: "Cadastrar manualmente", to: "/app/leads" },
    ],
    tips: [
      "A planilha pode ter colunas em português: Nome, Telefone, Bairro, Orçamento, Dormitórios. Eu reconheço.",
      "Se preferir falar comigo: 'nova lead Marcos, quer apto 2 dorm em Pinheiros até 800k, financia pelo banco' e eu cadastro na hora.",
    ],
  },
  {
    n: 3,
    icon: Building2,
    title: "Cadastre seus imóveis",
    minutes: "3 min",
    intro: "Bote pelo menos uns 5 imóveis da sua carteira. Quando você cadastra um lead, eu já cruzo com os imóveis compatíveis automaticamente.",
    actions: [
      { text: "Adicionar imóvel", to: "/app/imoveis", primary: true },
    ],
    tips: [
      "Pode subir sem foto. Depois você completa quando tiver tempo.",
      "Os campos importantes são: tipo, bairro, dormitórios, valor. O resto é bônus.",
    ],
  },
  {
    n: 4,
    icon: Sparkles,
    title: "Pegue sua primeira copy pronta",
    minutes: "1 min",
    intro: "Agora é a parte boa. Me peça uma copy pra qualquer situação e eu entrego o texto pronto pra você copiar e colar no WhatsApp do cliente.",
    actions: [
      { text: "Conversar comigo agora", to: "/app/chat", primary: true },
    ],
    tips: [
      "Exemplos do que pedir: 'me dá uma copy de primeiro contato pra Ana', 'copy pra reativar lead frio', 'copy pós-visita pra cliente que achou caro'.",
      "Cada copy vem personalizada com o nome do cliente, contexto e gatilho de urgência. Você não escreve do zero nunca mais.",
    ],
  },
  {
    n: 5,
    icon: TrendingUp,
    title: "Use o funil pra organizar o jogo",
    minutes: "2 min",
    intro: "O funil de 5 etapas separa quem é só curiosidade de quem tá perto de fechar. Use ele todo dia, é onde o dinheiro mora.",
    actions: [
      { text: "Ver meu funil", to: "/app/leads", primary: true },
    ],
    tips: [
      "Mover um lead de etapa é só arrastar no Kanban. Eu acompanho e ajusto os lembretes automaticamente.",
      "Quando uma proposta entra, eu cobro follow-up diário. Quando fecha, eu te entrego a celebração merecida.",
    ],
  },
];

const PRO_TIPS = [
  {
    icon: Mic,
    title: "Use áudio sempre que puder",
    desc: "Falar é 3x mais rápido que digitar. Você no carro, no almoço, entre uma visita e outra, manda áudio. Eu transcrevo e cadastro tudo certinho em português.",
  },
  {
    icon: Bell,
    title: "Configure o horário do briefing",
    desc: "Toda manhã no horário que você escolher, eu te mando um resumo do que precisa de atenção naquele dia. Tom de briefing de vendas, não de lista de tarefas.",
    link: "/app/config",
  },
  {
    icon: Zap,
    title: "Me peça orientação de negociação",
    desc: "Tô com impasse? Cliente sumiu? Proprietário irredutível? Manda a situação que eu analiso, sugiro estratégia e entrego a copy pra você destravar.",
  },
  {
    icon: Phone,
    title: "Pergunte sobre sua própria carteira",
    desc: "Coisas como 'quem tá há mais de 7 dias sem contato', 'me lembra o que a Fabíola queria', 'quantos leads tenho na etapa proposta'. Eu sei tudo que tá cadastrado.",
  },
];

export default function Onboarding() {
  const { user } = useAuth();
  const first = user?.name?.split(" ")[0] || "Corretor";

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Hero */}
      <div className="relative bg-[#1B3A2F] text-white px-5 lg:px-10 pt-16 pb-20 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#D4651A]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-full text-xs font-semibold uppercase tracking-[0.18em] mb-6">
            <Play className="w-3 h-3 text-[#D4651A]" />
            Guia de boas-vindas
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Bem-vindo, {first}.
            <br />
            <span className="text-[#D4651A]">Bora destravar sua carteira</span>.
          </h1>
          <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl">
            Aqui vai o caminho mais curto pra você sair de zero e já estar usando o estatebroker.AI
            com tudo que ele tem. Cinco passos. Quinze minutos. Pronto.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 lg:px-10 -mt-12 relative space-y-6">
        {/* Steps */}
        {STEPS.map((s) => <StepCard key={s.n} step={s} />)}

        {/* Pro tips */}
        <div className="pt-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-3 text-center">
            Como extrair o máximo
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A2F] tracking-tight text-center mb-10 leading-[1.05]">
            Quatro hábitos que viram comissão.
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {PRO_TIPS.map((tip, i) => <ProTip key={i} {...tip} />)}
          </div>
        </div>

        {/* Final CTA */}
        <Card className="bg-[#1B3A2F] text-white border-0 p-8 lg:p-12 rounded-3xl mt-10 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#D4651A]/20 rounded-full blur-3xl" />
          <div className="relative">
            <h3 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              Bora começar. <span className="text-[#D4651A]">Próxima venda</span> tá te esperando.
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
              <Link to="/app/chat">
                <Button className="bg-[#D4651A] hover:bg-[#A04E10] px-7 py-6 rounded-full font-semibold text-base" data-testid="onboarding-start-chat">
                  Falar com o estatebroker.AI <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/app">
                <Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 px-7 py-6 rounded-full font-semibold text-base">
                  Ir pro painel
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        <div className="text-center text-sm text-stone-500 py-8">
          Travou em algum passo? <Link to="/app/suporte" className="text-[#1B3A2F] font-semibold hover:underline">Abre um chamado no suporte</Link>, a gente resolve.
        </div>
      </div>
    </div>
  );
}

function StepCard({ step }) {
  const Icon = step.icon;
  return (
    <Card data-testid={`onboarding-step-${step.n}`} className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-md transition">
      <div className="flex flex-col sm:flex-row">
        <div className="bg-[#1B3A2F]/5 sm:w-32 flex items-center justify-center py-5 sm:py-0 border-b sm:border-b-0 sm:border-r border-stone-100">
          <div className="text-center">
            <div className="font-mono text-xs text-stone-500 tracking-widest mb-1">PASSO</div>
            <div className="font-heading text-5xl font-extrabold text-[#1B3A2F]">{String(step.n).padStart(2, '0')}</div>
          </div>
        </div>
        <div className="flex-1 p-6 lg:p-8">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#D4651A]/10 text-[#D4651A] flex items-center justify-center">
                <Icon className="w-5 h-5" />
              </div>
              <h2 className="font-heading text-xl font-bold text-[#1C1C19]">{step.title}</h2>
            </div>
            <Badge variant="outline" className="text-xs">{step.minutes}</Badge>
          </div>
          <p className="text-stone-600 mt-3 leading-relaxed">{step.intro}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {step.actions.map((a, i) => (
              <Link key={i} to={a.to}>
                <Button
                  data-testid={`onboarding-step-${step.n}-action-${i}`}
                  variant={a.primary ? "default" : "outline"}
                  size="sm"
                  className={a.primary ? "bg-[#1B3A2F] hover:bg-[#244A3C] text-white" : ""}
                >
                  {a.text}
                  {a.primary && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
                </Button>
              </Link>
            ))}
          </div>

          {step.tips?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-stone-100 space-y-2">
              {step.tips.map((t, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-stone-600">
                  <Check className="w-3.5 h-3.5 text-[#1B3A2F] mt-1 flex-shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function ProTip({ icon: Icon, title, desc, link }) {
  const Inner = (
    <Card className="bg-white rounded-2xl border border-stone-200 p-6 hover:border-[#1B3A2F] hover:-translate-y-1 transition-all duration-300 h-full">
      <div className="w-11 h-11 rounded-xl bg-[#1B3A2F] text-white flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-heading font-bold text-base text-[#1C1C19]">{title}</h3>
      <p className="text-sm text-stone-600 mt-2 leading-relaxed">{desc}</p>
      {link && (
        <div className="text-xs font-semibold text-[#D4651A] mt-3 inline-flex items-center gap-1">
          Configurar agora <ArrowRight className="w-3 h-3" />
        </div>
      )}
    </Card>
  );
  return link ? <Link to={link}>{Inner}</Link> : Inner;
}
